import { inject, Injectable, signal, TemplateRef } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { TellentAssistantThreadsService } from '@core/tellent-assistant-logic';
import type { ThreadSummary } from '@core/tellent-assistant-types';
import { ThreadMenuItemId, threadMenuGroups } from '@core/tellent-assistant-ui';
import {
  injectTellentMobileSheetPresenter,
  TellentMobileOptionsSheetComponent,
  TellentMobileSheetOption,
  TellentMobileSheetOptionIconContext,
  TellentMobileSheetPresenter,
} from '@core/tellent-mobile-ui';
import { IconName } from '@core/user-interface-legacy';

/**
 * Touch replacement for the thread drawer's hover kebab menu: presents the
 * shared thread menu (threadMenuGroups) as a bottom sheet via the
 * host-provided mobile sheet presenter, and applies the chosen action.
 */
@Injectable({ providedIn: 'root' })
export class ThreadOptionsSheetService {
  private readonly i18n = inject(I18nService);
  private readonly threadsService = inject(TellentAssistantThreadsService);
  private readonly presenter: TellentMobileSheetPresenter | null =
    injectTellentMobileSheetPresenter({ optional: true });

  private readonly thread = signal<ThreadSummary | null>(null);

  public iconName(option: TellentMobileSheetOption): IconName {
    const thread = this.thread();
    const item = thread
      ? threadMenuGroups(thread)
          .flat()
          .find(menuItem => menuItem.id === option.id)
      : undefined;
    return item?.icon ?? 'pin';
  }

  public apply(id: ThreadMenuItemId, thread: ThreadSummary): void {
    if (id === 'pin') {
      this.threadsService.setThreadPinned(thread.id, !thread.pinned);
    } else if (id === 'remove') {
      this.threadsService.deleteThread(thread.id);
    }
  }

  public async open(
    threadId: string,
    iconTemplate: TemplateRef<TellentMobileSheetOptionIconContext>,
  ): Promise<void> {
    const thread = this.threadsService.threads().find(t => t.id === threadId);
    if (!thread || !this.presenter) return;
    this.thread.set(thread);
    const groups = threadMenuGroups(thread);

    const handle = await this.presenter.createSheet({
      component: TellentMobileOptionsSheetComponent,
      background: 'neutral',
      canDismiss: true,
      componentProps: {
        optionGroups: groups.map(group =>
          group.map(item => ({
            id: item.id,
            label: this.i18n.translate(item.labelKey),
            destructive: item.destructive,
          })),
        ),
        iconTemplate,
        selectHandler: (option: TellentMobileSheetOption) => void handle.dismiss(option),
      },
    });
    await handle.present();

    const { data } = await handle.onWillDismiss<TellentMobileSheetOption>();
    const selected = groups.flat().find(item => item.id === data?.id);
    if (selected) {
      this.apply(selected.id, thread);
    }
  }
}
